import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useState, useEffect } from "react";
import { showErrorSnackbar } from "../../actprep-ui/utils/feedback";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function TestQuestionImageForm({
  open,
  onClose,
  onSave,
  existingImage = null,
  allImages = [],
}) {
  const isEditing = !!existingImage;
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    image_type: "Primary",
    has_diagram: false,
  });

  // Initialize form data when editing
  useEffect(() => {
    if (existingImage) {
      setFormData({
        image_type: existingImage.image_type || "Primary",
        has_diagram: existingImage.has_diagram || false,
      });
      setImagePreview(existingImage.image);
    } else {
      // Default to Contextual if Primary already exists
      const primaryExists = allImages.some(
        (img) => img.image_type === "Primary" && img.action !== "DELETE"
      );
      setFormData({
        image_type: primaryExists ? "Contextual" : "Primary",
        has_diagram: false,
      });
    }
  }, [existingImage, allImages, open]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setImageFile(null);
      setImagePreview(null);
      setFormData({
        image_type: "Primary",
        has_diagram: false,
      });
    }
  }, [open]);

  const handleFileUpload = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = () => {
    // Validation for new images
    if (!isEditing && !imageFile) {
      showErrorSnackbar("Please upload an image");
      return;
    }

    // Check Primary constraint
    const otherPrimaryExists = allImages.some(
      (img) =>
        img.uuid !== existingImage?.uuid &&
        img.image_type === "Primary" &&
        img.action !== "DELETE"
    );

    if (formData.image_type === "Primary" && otherPrimaryExists) {
      showErrorSnackbar(
        "Only one Primary image is allowed. Please change the existing Primary image to Contextual first."
      );
      return;
    }

    // Prepare data to send back
    const imageData = {
      ...formData,
      file: imageFile,
      preview: imagePreview,
    };

    if (isEditing) {
      imageData.uuid = existingImage.uuid;
      imageData.image = existingImage.image;
    }

    onSave(imageData);
    onClose();
  };

  const hasPrimaryImage = allImages.some(
    (img) =>
      img.uuid !== existingImage?.uuid &&
      img.image_type === "Primary" &&
      img.action !== "DELETE"
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={(theme) => ({
        zIndex: theme.zIndex.modal + 2,
      })}
    >
      <DialogTitle>{isEditing ? "Edit Image" : "Add New Image"}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* File Upload Button */}
          {!isEditing && (
            <Button
              component="label"
              variant="contained"
              startIcon={<CloudUpload />}
              fullWidth
            >
              {imageFile ? "Change Photo" : "Upload Photo"}
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
              />
            </Button>
          )}

          {/* Image Preview */}
          {imagePreview && (
            <>
              <Box sx={{ mt: 2, mb: 2 }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ width: "100%", borderRadius: "8px" }}
                />
              </Box>

              {/* Info Alert */}
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Primary:</strong> The actual question (only one
                allowed). <strong>Contextual:</strong> Supporting context like
                passages.
              </Alert>

              {/* Image Type Select */}
              <FormControl fullWidth variant="standard" sx={{ mb: 2 }}>
                <InputLabel>Image Type</InputLabel>
                <Select
                  value={formData.image_type}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      image_type: e.target.value,
                    }))
                  }
                  label="Image Type"
                  MenuProps={{
                    sx: (theme) => ({
                      zIndex: theme.zIndex.modal + 3,
                    }),
                  }}
                >
                  <MenuItem value="Primary" disabled={hasPrimaryImage}>
                    Primary - Question Image{" "}
                    {hasPrimaryImage && "(Already exists)"}
                  </MenuItem>
                  <MenuItem value="Contextual">
                    Contextual - Supporting Material
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Has Diagram Switch */}
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.has_diagram}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        has_diagram: e.target.checked,
                      }))
                    }
                  />
                }
                label="Has Diagram"
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!imagePreview}
        >
          {isEditing ? "Save Changes" : "Add Image"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
